import { useCallback, useEffect, useState } from "react";
import { useLocation } from "react-router";
import { useTranslation } from "react-i18next";

// Function to extract route parameters
const extractRouteParams = (pathname: string): Record<string, string> => {
  const params: Record<string, string> = {};

  // Extract orderId from edit order route
  const orderEditMatch = /\/orders\/edit\/([^/]+)/.exec(pathname);
  if (orderEditMatch?.[1]) {
    params.orderId = orderEditMatch[1];
  }

  // Extract tracking number from tracking route
  const trackingMatch = /\/tracking\/([^/]+)/.exec(pathname);
  if (trackingMatch?.[1]) {
    params.trackingNumber = trackingMatch[1];
  }

  return params;
};

// Function to generate breadcrumb title
const generateBreadcrumbTitle = (
  pathname: string,
  params: Record<string, string>,
  routeSegments: Record<string, string>,
  homeTitle: string
): string => {
  // Handle home page
  if (pathname === "/") {
    return `${homeTitle} | TooEZ`;
  }

  // Split the path into segments
  const segments = pathname.split("/").filter(Boolean);

  // Map segments to their display names
  const breadcrumbs = segments.map((segment) => {
    // Check if this segment is a parameter (like an ID)
    if (segment === params.orderId && params.orderId) {
      return `#${params.orderId}`;
    }
    if (segment === params.trackingNumber && params.trackingNumber) {
      return `#${params.trackingNumber}`;
    }

    // Return the display name or the segment itself if not found
    return routeSegments[segment] || segment;
  });

  // Join the breadcrumbs with a separator and add the site name
  return `${breadcrumbs.join(" › ")} | TooEZ`;
};

// Interface for order data
interface OrderData {
  id: string;
  name?: string;
  reference?: string;
  status?: string;
}

// Interface for API response
interface OrderResponse {
  id: string;
  name?: string;
  reference?: string;
  status?: string;
}

export const DynamicTitle = () => {
  const location = useLocation();
  const { t } = useTranslation("app");
  const [orderData, setOrderData] = useState<OrderData | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  // Function to fetch order data
  const fetchOrderData = useCallback(async (orderId: string): Promise<void> => {
    try {
      setIsLoading(true);
      const response = await fetch(`/api/orders/${orderId}`);
      if (response.ok) {
        const data = (await response.json()) as OrderResponse;
        setOrderData(data);
      }
    } catch (error) {
      console.error("Error fetching order data:", error);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    const routeSegments: Record<string, string> = {
      dashboard: t("titles.shipments"),
      orders: t("titles.shipments"),
      history: t("titles.history"),
      invoices: t("titles.invoices"),
      vouchers: t("titles.vouchers"),
      carriers: t("titles.carriers"),
      "my-address-book": t("titles.addressBook"),
      "my-packaging": t("titles.myPackaging"),
      billing: t("titles.billing"),
      "payment-methods": t("titles.paymentMethods"),
      account: t("titles.profile"),
      "business-account": t("titles.businessAccount"),
      settings: t("titles.settings"),
      create: t("titles.create"),
      edit: t("titles.edit"),
      "new-shipment": t("titles.newShipment"),
      details: t("titles.details"),
      pricing: t("titles.pricing"),
      payment: t("titles.payment"),
      confirmation: t("titles.confirmation"),
      "bulk-ship": t("titles.bulkShip"),
      tracking: t("titles.tracking"),
      track: t("titles.tracking"),
      onboarding: t("titles.getStarted"),
      "step-1": t("titles.step1"),
      "step-2": t("titles.step2"),
      success: t("titles.complete"),
      "sign-in": t("titles.signIn"),
      "sign-up": t("titles.signUp"),
      "how-it-works": t("titles.howItWorks"),
      coverage: t("titles.coverage"),
      "become-a-partner": t("titles.partners"),
      "work-for-us": t("titles.careers"),
      help: t("titles.help"),
      "privacy-policy": t("titles.privacy"),
      "terms-of-use": t("titles.terms"),
      unauthorized: t("titles.unauthorized"),
    };

    // Get the current path
    const { pathname } = location;

    // Reset order data when route changes
    setOrderData(null);

    // Extract route parameters
    const params = extractRouteParams(pathname);

    // Generate breadcrumb title
    document.title = generateBreadcrumbTitle(
      pathname,
      params,
      routeSegments,
      t("titles.home")
    );

    // Handle dynamic routes with parameters that need additional data
    if (pathname.startsWith("/orders/edit/") && params.orderId) {
      // Set loading state in title
      document.title = `${t("titles.shipments")} › ${t("titles.edit")} › ${t("titles.loading")} | TooEZ`;
      // Fetch order data for a more descriptive title
      void fetchOrderData(params.orderId);
    }
  }, [fetchOrderData, location, t]);

  // Update title when order data is loaded
  useEffect(() => {
    if (
      orderData &&
      location.pathname.includes(`/orders/edit/${orderData.id}`) &&
      !isLoading
    ) {
      const orderName = orderData.name ?? orderData.reference ?? orderData.id;
      const orderStatus = orderData.status ? ` (${orderData.status})` : "";
      document.title = `${t("titles.shipments")} › ${t("titles.edit")} › ${orderName}${orderStatus} | TooEZ`;
    }
  }, [orderData, location, isLoading, t]);

  // This component doesn't render anything
  return null;
};

export default DynamicTitle;
